import LocalizedStrings from "react-localization";
import { useEffect, useState } from "react";
import { Badge, Button, Flex, Grid, Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";
import { useNavigate } from "react-router-dom";

import ServiceOrderInfo from "components/serviceOrders/ServiceOrderInfo";
import MaintenanceRoutinesInfo from "components/maintenanceRoutines/MaintenanceRoutinesInfo";
import { getServiceOrder, listEquipment } from "graphql/queries";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { CCard } from "@coreui/react";
import { OrderProvider } from "components/serviceOrders/context/OrderContext";

const ServiceOrderResume = ({ serviceOrderId, onSuccess }) => {
  const navigate = useNavigate();
  const [serviceOrder, setServiceOrder] = useState();
  const [loading, setLoading] = useState(false);

  const onExport = async () => {
    try {
      setLoading(true);

      const el = document.querySelector(
        `#export-pdf-order-${serviceOrder.code}`
      );
      const opt = {
        margin: 0.2,
        filename: `order-${serviceOrder.code}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      await html2pdf().set(opt).from(el).save();

      toast.success(strings.successExported);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  async function fetchServiceOrder(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrder, { id })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      if (!serviceOrderItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/service-orders");
        return;
      }

      if (serviceOrderItem.equipments?.items?.length > 0) {
        const equipmentsData = (
          await API.graphql(
            graphqlOperation(listEquipment, {
              filter: {
                or: serviceOrderItem.equipments?.items?.map((eq) => ({
                  id: { eq: eq.equipmentId },
                })),
              },
            })
          )
        ).data.listEquipment.items;
        serviceOrderItem.equipments.items =
          equipmentsData?.map((eq) => ({ id: eq.id, name: eq.name })) ?? [];
      }

      setServiceOrder({
        ...serviceOrderItem,
        maintenanceRoutines:
          serviceOrderItem.maintenanceRoutines?.items?.map(
            (t) => t.maintenanceRoutineId
          ) ?? [],
        equipments: serviceOrderItem.equipments?.items ?? [],
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  useEffect(() => {
    fetchServiceOrder(serviceOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderId]);

  if (!serviceOrder) return <Loader />;

  return (
    <>
      <Grid gap="15px" id={`export-pdf-order-${serviceOrder.code}`}>
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <ServiceOrderInfo serviceOrder={serviceOrder} />
        </Grid>
        <Grid templateColumns={{ large: "1fr" }} gap="15px">
          <CCard
            className="p-3"
            borderRadius="medium"
            variation="outlined"
            style={{ height: "100%" }}
          >
            <Grid gap="5px">
              <span className="fs-5">{strings.equipments}</span>
              {serviceOrder.equipments.length > 0 ? (
                <Flex rowGap="10px" columnGap="10px" wrap="wrap">
                  {serviceOrder.equipments.map((equipment, index) => (
                    <Badge key={index}>{equipment.name}</Badge>
                  ))}
                </Flex>
              ) : (
                strings.noEquipments
              )}
            </Grid>
          </CCard>
          <OrderProvider serviceOrderID={serviceOrderId} viewType="export">
            <MaintenanceRoutinesInfo
              maintenanceRoutineIDs={serviceOrder.maintenanceRoutines}
            />
          </OrderProvider>
        </Grid>
        <Flex justifyContent="end" data-html2canvas-ignore>
          <Button onClick={onExport} variation="primary">
            {strings.exportResume}
          </Button>
        </Flex>
      </Grid>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    equipments: "Equipos asociados",
    noEquipments: "Sin equipos asociados...",
    exportResume: "Exportar Resumen",
    successExported: "Orden de Servicio exportada con éxito",
    responses: {
      unexpectedError: "Ha ocurrido un error inesperado",
      errorFetch: "Error al consultar la orden",
    },
  },
});

export default ServiceOrderResume;
